/**
 * SYSTEM → CONTROLS -- the approvals and the access list that govern the issue
 * console, in one place (owner ruling 2026-09-27):
 *
 *   Approvals         scope requests (a developer asking for backend/infra),
 *                     runs the AI gate refused, and signed-off fixes waiting to
 *                     ship (the board that used to be /app-issues' tab).
 *   Developer access  who may send Ruby at what: UI / Backend / Infrastructure,
 *                     approvers, and (later) repo limits -- people picked by
 *                     browsing the platform-admin user pool.
 *
 * The console's Tools tab only SHOWS "your access"; everything is changed here.
 * Ruby-on-light like the rest of super-admin. Custom controls only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  KeyRound, ShieldCheck, AlertTriangle, Check, X as XIcon, Search, UserPlus, Trash2, Lock, ExternalLink,
} from 'lucide-react';
import { StaffPage, StaffSubtabs } from '../../components/staff/StaffChrome';
import { RubyLoader } from '../../components/RubyLoader';
import { issuesApi, AdminListFilters } from '../../services/issuesApi';
import {
  consoleApi, controlsApi, AccessControls, DevAccess, DevAccessEntry, PoolUser, ScopeRequest, ScopeViolation, WorkScope,
} from '../../services/consoleApi';
import { ApprovalBoard } from './ApprovalBoard';

const RUBY = '#DC143C';

const LEVELS: { id: DevAccess; label: string; hint: string }[] = [
  { id: 'ui', label: 'UI only', hint: 'Screens, components, styles, copy' },
  { id: 'backend', label: 'Backend', hint: 'UI + services, handlers, shared types, dependencies' },
  { id: 'infra', label: 'Infrastructure', hint: 'Everything: stacks, queues, CI, native projects' },
];
const LEVEL_LABEL: Record<WorkScope, string> = { ui: 'UI only', backend: 'Backend', infra: 'Infrastructure' };

const fmtWhen = (ts?: number | null) =>
  ts ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/* ------------------------------------------------------------ small controls */

const LevelPicker: React.FC<{ value: DevAccess; onChange: (v: DevAccess) => void; disabled?: boolean; label: string }> = ({
  value, onChange, disabled, label,
}) => (
  <div role="radiogroup" aria-label={label} className="inline-flex rounded-xl border border-stone-200 bg-stone-50 p-1 gap-1">
    {LEVELS.map((l) => {
      const on = value === l.id;
      return (
        <button
          key={l.id}
          type="button"
          role="radio"
          aria-checked={on}
          disabled={disabled}
          title={l.hint}
          onClick={() => onChange(l.id)}
          className={`min-h-[36px] px-3 rounded-lg text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition-colors ${
            on
              ? l.id === 'ui'
                ? 'bg-emerald-600 text-white'
                : l.id === 'backend'
                  ? 'bg-amber-500 text-white'
                  : 'text-white'
              : 'text-stone-600 hover:bg-white'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={on && l.id === 'infra' ? { background: RUBY } : undefined}
        >
          {l.id !== 'ui' && <AlertTriangle size={12} />}
          {l.label}
        </button>
      );
    })}
  </div>
);

const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }> = ({
  on, onChange, label, disabled,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={`min-h-[44px] inline-flex items-center gap-2 text-[12.5px] font-medium ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span className="relative inline-block w-10 h-6 rounded-full transition-colors" style={{ background: on ? RUBY : '#d6d3d1' }}>
      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all" style={{ left: on ? 18 : 2 }} />
    </span>
    <span className={on ? 'text-stone-900' : 'text-stone-500'}>Approver</span>
  </button>
);

/* ------------------------------------------------------------ approvals tab */

type RequestRow = { id: string; title: string | null; status: string; req: ScopeRequest };
type ViolationRow = { id: string; title: string | null; v: ScopeViolation };

const IN_PLAY: NonNullable<AdminListFilters['status']>[] = ['new', 'in_progress', 'deploying', 'awaiting_retest', 'awaiting_owner_approval'];

const ApprovalsTab: React.FC<{ onCounts: (n: number) => void }> = ({ onCounts }) => {
  const [rows, setRows] = useState<{ requests: RequestRow[]; violations: ViolationRow[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [done, setDone] = useState<Record<string, 'approved' | 'denied'>>({});

  const load = useCallback(async () => {
    setErr(null);
    try {
      const pages = await Promise.all(IN_PLAY.map((status) => issuesApi.list({ status, limit: 100 })));
      const all = pages.flatMap((p: any) => p.issues || []).filter((r: any) => (r.sourceStage ?? 'dev') !== 'prod');
      const requests = all
        .filter((r: any) => r.scopeRequest?.state === 'pending')
        .map((r: any) => ({ id: r.id, title: r.title ?? null, status: r.status, req: r.scopeRequest }))
        .sort((a, b) => b.req.at - a.req.at);
      const violations = all
        .filter((r: any) => r.scopeViolation && typeof r.scopeViolation === 'object')
        .map((r: any) => ({ id: r.id, title: r.title ?? null, v: r.scopeViolation }))
        .sort((a, b) => b.v.at - a.v.at);
      setRows({ requests, violations });
      onCounts(requests.length);
    } catch (e) {
      setErr(errText(e));
    }
  }, [onCounts]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (id: string, approve: boolean) => {
    setBusy(id);
    setErr(null);
    try {
      await consoleApi.decideApproval(id, approve, notes[id]?.trim() || undefined);
      setDone((d) => ({ ...d, [id]: approve ? 'approved' : 'denied' }));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const consoleHref = (id: string) => `/app-issues/${encodeURIComponent(`dev:${id}`)}/console`;

  return (
    <div className="space-y-6">
      {err && <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{err}</div>}

      <section>
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="text-[16px] font-bold text-stone-900">Scope requests</h2>
          <span className="text-[12.5px] text-stone-500">developers asking for more than their access allows</span>
        </div>
        {!rows ? (
          <div className="relative h-24 bg-white border border-stone-200 rounded-2xl"><RubyLoader variant="overlay" /></div>
        ) : rows.requests.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center text-stone-500 text-[13px]">
            <Check size={22} className="mx-auto text-stone-300 mb-2" />No scope requests waiting.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.requests.map((r) => {
              const d = done[r.id];
              return (
                <div key={r.id} className="bg-white border border-stone-200 rounded-2xl p-4 flex flex-col lg:flex-row gap-4" style={{ borderLeft: `4px solid ${r.req.scope === 'infra' ? RUBY : '#f59e0b'}` }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md"
                        style={{ background: r.req.scope === 'infra' ? '#FFF1F3' : '#FEF3C7', color: r.req.scope === 'infra' ? '#9F1239' : '#92400E' }}>
                        <AlertTriangle size={12} /> {LEVEL_LABEL[r.req.scope]}
                      </span>
                      <Link to={consoleHref(r.id)} className="text-[14px] font-semibold text-stone-900 hover:underline truncate">
                        {r.title || 'Untitled issue'}
                      </Link>
                      <span className="text-[12px] text-stone-400 font-mono">#{r.id}</span>
                    </div>
                    <div className="text-[12.5px] text-stone-500 mt-1">
                      {r.req.byName || 'A developer'} · {fmtWhen(r.req.at)}
                    </div>
                    <div className="text-[13.5px] text-stone-800 mt-2">“{r.req.reason}”</div>
                  </div>
                  <div className="flex flex-col gap-2 lg:w-[320px]">
                    {d ? (
                      <div className={`text-[13px] font-semibold ${d === 'approved' ? 'text-emerald-700' : 'text-red-700'}`}>
                        {d === 'approved' ? 'Approved' : 'Denied'} — the developer sees it in the console.
                      </div>
                    ) : (
                      <>
                        <input
                          value={notes[r.id] || ''}
                          onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                          placeholder="Note to the developer (optional)"
                          aria-label={`Note for ${r.title || r.id}`}
                          className="min-h-[44px] px-3 rounded-xl border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-200"
                        />
                        <div className="flex gap-2">
                          <button type="button" disabled={busy === r.id} onClick={() => decide(r.id, true)}
                            className="flex-1 min-h-[44px] rounded-xl bg-emerald-600 text-white text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                            <Check size={15} /> Approve {LEVEL_LABEL[r.req.scope]}
                          </button>
                          <button type="button" disabled={busy === r.id} onClick={() => decide(r.id, false)}
                            className="min-h-[44px] px-4 rounded-xl border border-stone-300 text-stone-700 text-[13px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                            <XIcon size={15} /> Deny
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {rows && rows.violations.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-[16px] font-bold text-stone-900">Blocked by the AI scope gate</h2>
            <span className="text-[12.5px] text-stone-500">Ruby changed files past what it was approved for — refused, nothing pushed</span>
          </div>
          <div className="space-y-2">
            {rows.violations.map((r) => (
              <div key={r.id} className="bg-white border border-red-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertTriangle size={15} style={{ color: RUBY }} />
                  <Link to={consoleHref(r.id)} className="text-[14px] font-semibold text-stone-900 hover:underline">{r.title || 'Untitled issue'}</Link>
                  <span className="text-[12px] text-stone-400 font-mono">#{r.id}</span>
                  <span className="text-[12px] text-stone-500">· attempt {r.v.attempt} · {fmtWhen(r.v.at)}</span>
                </div>
                <div className="text-[13px] text-stone-700 mt-1">
                  Approved for <strong>{LEVEL_LABEL[r.v.allowed]}</strong>, changed <strong style={{ color: RUBY }}>{LEVEL_LABEL[r.v.impact]}</strong> files:
                  <span className="font-mono text-[12px] text-stone-600"> {r.v.files.slice(0, 6).join(', ')}{r.v.files.length > 6 ? ', …' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-baseline gap-3 mb-3">
          <h2 className="text-[16px] font-bold text-stone-900">Ready to ship</h2>
          <span className="text-[12.5px] text-stone-500">tested and signed off, waiting for your approval</span>
        </div>
        <ApprovalBoard />
      </section>
    </div>
  );
};

/* ------------------------------------------------------ user-pool browser */

const UserBrowser: React.FC<{
  existing: Set<string>;
  onPick: (u: PoolUser) => void;
  onClose: () => void;
}> = ({ existing, onPick, onClose }) => {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<PoolUser[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const reqId = useRef(0);

  const load = useCallback(async (query: string, cursor?: string | null) => {
    const my = ++reqId.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await controlsApi.browseUsers(query || undefined, cursor);
      if (my !== reqId.current) return;
      setUsers((prev) => (cursor ? [...prev, ...r.users] : r.users));
      setNext(r.next);
    } catch (e) {
      if (my === reqId.current) setErr(errText(e));
    } finally {
      if (my === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { void load(q.trim()); }, 250);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[8vh]" role="dialog" aria-modal="true" aria-label="Pick a developer">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-3" style={{ background: '#0a0a0a' }}>
          <UserPlus size={18} className="text-white" />
          <div className="text-white font-semibold text-[15px] flex-1">Add a developer</div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-stone-300 hover:text-white">
            <XIcon size={18} />
          </button>
        </div>
        <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${RUBY}, #9F1239)` }} />
        <div className="p-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the admin user pool by email…"
              aria-label="Search users by email"
              className="w-full min-h-[44px] pl-9 pr-3 rounded-xl border border-stone-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-rose-200"
            />
          </div>
          <div className="text-[11.5px] text-stone-400 mt-1.5">Matches the start of an email. People who sign in to this admin.</div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto border-t border-stone-100">
          {err && <div role="alert" className="m-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{err}</div>}
          {!err && users.length === 0 && !loading && <div className="p-6 text-center text-stone-500 text-[13px]">No users match.</div>}
          {users.map((u) => {
            const added = existing.has(u.userId) || existing.has(u.email.toLowerCase());
            return (
              <button
                key={u.userId}
                type="button"
                disabled={added || !u.enabled}
                onClick={() => onPick(u)}
                className="w-full min-h-[56px] px-5 py-2.5 flex items-center gap-3 text-left border-b border-stone-100 hover:bg-rose-50/50 disabled:hover:bg-transparent disabled:opacity-60"
              >
                <span className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0" style={{ background: RUBY }}>
                  {(u.name || u.email).slice(0, 1).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-semibold text-stone-900 truncate">{u.name || u.email}</span>
                  {u.name && <span className="block text-[12px] text-stone-500 truncate">{u.email}</span>}
                </span>
                {!u.enabled && <span className="text-[11px] font-semibold text-stone-500 bg-stone-100 rounded-md px-2 py-0.5">Disabled</span>}
                {u.status && u.status !== 'CONFIRMED' && u.enabled && (
                  <span className="text-[11px] font-semibold text-amber-800 bg-amber-100 rounded-md px-2 py-0.5">{u.status.replace(/_/g, ' ').toLowerCase()}</span>
                )}
                {added && <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-100 rounded-md px-2 py-0.5">Added</span>}
              </button>
            );
          })}
          {loading && <div className="relative h-16"><RubyLoader variant="overlay" /></div>}
          {next && !loading && (
            <button type="button" onClick={() => load(q.trim(), next)} className="w-full min-h-[44px] text-[13px] font-semibold" style={{ color: RUBY }}>
              Load more
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------ developer access tab */

const AccessTab: React.FC = () => {
  const [data, setData] = useState<AccessControls | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [pending, setPending] = useState<{ user: PoolUser; level: DevAccess; approver: boolean } | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try { setData(await controlsApi.getAccess()); } catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async (key: string, w: Parameters<typeof controlsApi.setAccess>[0]) => {
    setSaving(key);
    setErr(null);
    try {
      const r = await controlsApi.setAccess(w);
      setData((d) => (d ? { ...d, entries: r.entries, enforcing: r.enforcing, approvers: r.approvers } : d));
      return true;
    } catch (e) {
      setErr(errText(e));
      return false;
    } finally {
      setSaving(null);
    }
  };

  const existing = useMemo(() => {
    const s = new Set<string>();
    (data?.entries || []).forEach((e) => { if (e.userId) s.add(e.userId); s.add(e.email.toLowerCase()); });
    return s;
  }, [data]);

  const entries = useMemo(
    () => [...(data?.entries || [])].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)),
    [data],
  );

  if (!data && !err) return <div className="relative h-40 bg-white border border-stone-200 rounded-2xl"><RubyLoader variant="overlay" /></div>;

  const writeOf = (e: DevAccessEntry, over: Partial<{ access: DevAccess | null; canApprove: boolean }>) => ({
    userId: e.userId ?? null, email: e.email, name: e.name ?? null,
    access: over.access === undefined ? e.access : over.access,
    canApprove: over.canApprove === undefined ? !!e.canApprove : over.canApprove,
    ...(e.repos ? { repos: e.repos } : {}),
  });

  const meInList = !!data && entries.some((e) => (e.userId && e.userId === data.you.userId) || (data.you.email && e.email === data.you.email.toLowerCase()));

  return (
    <div className="space-y-5">
      {err && <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{err}</div>}

      {data && !data.enforcing && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0" />
          <div className="flex-1 text-[13.5px] text-amber-900">
            <strong>Access control is off.</strong> Until an approver exists, everyone who signs in to this admin can send
            Ruby at infrastructure. Add yourself as the first approver to turn it on — from then on, anyone not listed is UI-only.
          </div>
          {!meInList && data.you.email && (
            <button
              type="button"
              disabled={saving !== null}
              onClick={() => save('me', { userId: data.you.userId, email: data.you.email!, name: data.you.name, access: 'infra', canApprove: true })}
              className="min-h-[44px] px-4 rounded-xl text-white text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              style={{ background: RUBY }}
            >
              <ShieldCheck size={15} /> Make me the first approver
            </button>
          )}
        </div>
      )}

      {data?.enforcing && (
        <div className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-3 text-[13px] text-stone-700">
          <Lock size={16} className="text-emerald-600" />
          Access control is <strong className="text-emerald-700">on</strong> · {data.approvers} approver{data.approvers === 1 ? '' : 's'} ·
          anyone not listed is UI-only · Ruby is held to the approved scope on every run.
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 flex items-center gap-3 border-b border-stone-100">
          <div className="flex-1">
            <div className="text-[15px] font-bold text-stone-900">Developers</div>
            <div className="text-[12.5px] text-stone-500">What each person may send Ruby to change without an approval</div>
          </div>
          <button type="button" onClick={() => setBrowsing(true)}
            className="min-h-[44px] px-4 rounded-xl text-white text-[13px] font-semibold inline-flex items-center gap-2" style={{ background: RUBY }}>
            <UserPlus size={15} /> Add developer
          </button>
        </div>

        {entries.length === 0 ? (
          <div className="p-8 text-center text-stone-500 text-[13px]">Nobody is listed yet.</div>
        ) : entries.map((e) => (
          <div key={e.key} className="px-5 py-3 border-b border-stone-100 flex flex-col xl:flex-row xl:items-center gap-3">
            <div className="flex items-center gap-3 xl:w-[300px] min-w-0">
              <span className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0" style={{ background: e.canApprove ? RUBY : '#78716c' }}>
                {(e.name || e.email).slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-stone-900 truncate flex items-center gap-1.5">
                  {e.name || e.email}
                  {e.canApprove && <ShieldCheck size={13} style={{ color: RUBY }} aria-label="Approver" />}
                </div>
                {e.name && <div className="text-[12px] text-stone-500 truncate">{e.email}</div>}
                {!e.userId && <div className="text-[11px] text-amber-700">matched by email</div>}
              </div>
            </div>
            <LevelPicker
              label={`Access level for ${e.email}`}
              value={e.access}
              disabled={saving !== null}
              onChange={(v) => save(e.key, writeOf(e, { access: v }))}
            />
            <Toggle
              label={`Approver: ${e.email}`}
              on={!!e.canApprove}
              disabled={saving !== null}
              onChange={(v) => save(e.key, writeOf(e, { canApprove: v, access: v ? 'infra' : e.access }))}
            />
            <div className="text-[12px] text-stone-500 xl:flex-1">
              <span className="inline-flex items-center gap-1 bg-stone-100 rounded-md px-2 py-1">
                {e.repos && e.repos.length ? e.repos.join(', ') : 'All repos'}
              </span>
              <span className="ml-2 text-stone-400">repo-level limits coming soon</span>
            </div>
            <div className="flex items-center gap-2">
              {confirmRemove === e.key ? (
                <>
                  <span className="text-[12.5px] text-stone-600">Remove?</span>
                  <button type="button" onClick={() => setConfirmRemove(null)} className="min-h-[44px] px-3 rounded-xl border border-stone-300 text-[12.5px]">Cancel</button>
                  <button type="button" disabled={saving !== null}
                    onClick={async () => { if (await save(e.key, writeOf(e, { access: null }))) setConfirmRemove(null); }}
                    className="min-h-[44px] px-3 rounded-xl text-white text-[12.5px] font-semibold" style={{ background: RUBY }}>
                    Remove
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => setConfirmRemove(e.key)} aria-label={`Remove ${e.email}`}
                  className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-stone-400 hover:text-red-600 hover:bg-red-50">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="text-[12px] text-stone-500 leading-relaxed">
        <strong className="text-stone-700">How the gate works:</strong> a developer can start a run only up to their level
        (above it, they request approval on the issue). The run is held to the scope it was started at: the fixer checks its
        changed files with the service before pushing, and the service checks again when it reports — a run that touched
        more is refused, nothing is pushed, and it shows under Approvals → Blocked.
      </div>

      {browsing && (
        <UserBrowser
          existing={existing}
          onClose={() => setBrowsing(false)}
          onPick={(u) => { setBrowsing(false); setPending({ user: u, level: 'ui', approver: false }); }}
        />
      )}

      {pending && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Set access">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-5 space-y-4">
            <div>
              <div className="text-[15px] font-bold text-stone-900">{pending.user.name || pending.user.email}</div>
              {pending.user.name && <div className="text-[12.5px] text-stone-500">{pending.user.email}</div>}
            </div>
            <div>
              <div className="text-[12px] font-semibold text-stone-600 mb-1.5">Access level</div>
              <LevelPicker label="Access level" value={pending.level} onChange={(v) => setPending({ ...pending, level: v })} />
              <div className="text-[12px] text-stone-500 mt-1.5">{LEVELS.find((l) => l.id === pending.level)?.hint}</div>
            </div>
            <Toggle label="Approver" on={pending.approver} onChange={(v) => setPending({ ...pending, approver: v, level: v ? 'infra' : pending.level })} />
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setPending(null)} className="min-h-[44px] px-4 rounded-xl border border-stone-300 text-[13px] font-semibold text-stone-700">Cancel</button>
              <button
                type="button"
                disabled={saving !== null}
                onClick={async () => {
                  const ok = await save('new', {
                    userId: pending.user.userId, email: pending.user.email, name: pending.user.name,
                    access: pending.level, canApprove: pending.approver,
                  });
                  if (ok) setPending(null);
                }}
                className="min-h-[44px] px-4 rounded-xl text-white text-[13px] font-semibold disabled:opacity-50" style={{ background: RUBY }}
              >
                {saving ? 'Saving…' : 'Give access'}
              </button>
            </div>
            {err && <div role="alert" className="text-[12.5px] text-red-700">{err}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------- the page */

const ControlsPage: React.FC = () => {
  const [tab, setTab] = useState<'approvals' | 'access'>('approvals');
  const [pendingCount, setPendingCount] = useState(0);
  return (
    <StaffPage
      section="System"
      icon={KeyRound}
      title="Controls"
      subtitle="Approvals and developer access for the Issue Console"
      tabs={
        <StaffSubtabs
          tabs={[
            { id: 'approvals', label: 'Approvals', badge: pendingCount },
            { id: 'access', label: 'Developer access' },
          ]}
          active={tab}
          onChange={(id) => setTab(id as 'approvals' | 'access')}
        />
      }
      actions={
        <Link to="/app-issues" className="min-h-[44px] px-4 rounded-xl border border-stone-300 bg-white text-[13px] font-semibold text-stone-700 inline-flex items-center gap-2">
          Issue Console <ExternalLink size={14} />
        </Link>
      }
      wide
    >
      {tab === 'approvals' ? <ApprovalsTab onCounts={setPendingCount} /> : <AccessTab />}
    </StaffPage>
  );
};

export default ControlsPage;
